import type { CmsShortcut, CmsAppShortcut, CmsGameShortcut } from '@nsftx/seven-client-retail-vue-sgp'
import { logService, ScanService } from '@nsftx/seven-client-retail-utils'
import { useShortcutsStore } from './shortcutsStore'
import type { ShortcutActionHandler, ShortcutActionName } from './shortcutsTypes'
import { getShortcutKeys } from './shortcutsUtils'

const LOG_PREFIX = '[shortcutsService]'

const shortcutHandlers = {
  global: new Map<ShortcutActionName, ShortcutActionHandler[]>(),
  local: new Map<ShortcutActionName, ShortcutActionHandler[]>(),
}

let shortcutsKeydownHandler: ((event: KeyboardEvent) => void) | null = null

const isGameShortcut = (shortcut: CmsShortcut): shortcut is CmsGameShortcut => (
  'game' in shortcut
)

const isAppShortcut = (shortcut: CmsShortcut): shortcut is CmsAppShortcut => (
  !isGameShortcut(shortcut)
)

const registerShortcutAction = (
  action: ShortcutActionName,
  callback: ShortcutActionHandler,
  local: boolean,
) => {
  const stacks = local ? shortcutHandlers.local : shortcutHandlers.global
  const stack = stacks.get(action) ?? []

  stack.push(callback)
  stacks.set(action, stack)

  return () => {
    const nextStack = stacks.get(action)
    if (!nextStack) {
      return
    }

    const index = nextStack.lastIndexOf(callback)
    if (index === -1) {
      return
    }

    nextStack.splice(index, 1)
    if (!nextStack.length) {
      stacks.delete(action)
    }
  }
}

const runShortcut = (
  stacks: Map<ShortcutActionName, ShortcutActionHandler[]>,
  shortcut: CmsShortcut,
) => {
  const shortcutAction = getShortcutAction(shortcut)
  const stack = stacks.get(shortcutAction)
  const handler = stack?.[stack.length - 1]
  if (!handler) {
    return false
  }

  handler(shortcut)
  return true
}

const getShortcutAction = (shortcut: CmsShortcut) => (
  isAppShortcut(shortcut) ? shortcut.action : shortcut.actionCode
)

const isLocalShortcut = (shortcut: CmsShortcut) => (
  isAppShortcut(shortcut) && shortcut.context === 'local'
)

const resolveShortcut = (shortcuts: CmsShortcut[]) => (
  shortcuts.find((shortcut) => (
    isLocalShortcut(shortcut) && shortcutHandlers.local.has(getShortcutAction(shortcut))
  ))
  ?? shortcuts.find((shortcut) => !isLocalShortcut(shortcut))
)

const dispatchShortcut = (shortcut: CmsShortcut) => {
  if (runShortcut(shortcutHandlers.local, shortcut)) {
    return
  }

  if (runShortcut(shortcutHandlers.global, shortcut)) {
    return
  }

  logService.warn(`${LOG_PREFIX} Shortcut handler not found.`, {
    code: 'S_SHORTCUTS_ACTION_HANDLER_NOT_FOUND',
    details: {
      action: getShortcutAction(shortcut),
    }
  })
}

const unbindShortcutKeys = () => {
  if (!shortcutsKeydownHandler) {
    return
  }

  window.removeEventListener('keydown', shortcutsKeydownHandler)
  shortcutsKeydownHandler = null
}

const dispatchShortcutKeydown = (e: KeyboardEvent) => {
  const finalKey = [
    ...new Set(
      [
        e.ctrlKey && 'ctrl',
        e.altKey && 'alt',
        e.shiftKey && 'shift',
        e.metaKey && 'meta',
        e.key?.toLowerCase(),
      ].filter(Boolean),
    ),
  ].join('+')

  const { getShortcutsByKey } = useShortcutsStore()
  const activeShortcuts = getShortcutsByKey(finalKey)
  const shortcut = resolveShortcut(activeShortcuts)

  if (!shortcut) {
    return
  }

  const shortcutAction = isAppShortcut(shortcut) ? shortcut.action : shortcut.actionCode
  const shortcutCategory = isAppShortcut(shortcut) ? shortcut.category : 'game'

  logService.debug(`${LOG_PREFIX} Shortcut for key ${finalKey} found.`, {
    code: 'S_SHORTCUTS_ACTION_FOUND',
    details: {
      key: finalKey,
      action: shortcutAction,
      category: shortcutCategory,
      active: shortcut.isActive,
    }
  })

  if (!shortcut.isActive) {
    return
  }

  if (isAppShortcut(shortcut)) {
    dispatchShortcut(shortcut)
  }
}

const handleShortcutKeydown = (e: KeyboardEvent) => {
  if (ScanService().isScanActive()) {
    return
  }

  dispatchShortcutKeydown(e)
}

const bindShortcutKeys = () => {
  unbindShortcutKeys()

  shortcutsKeydownHandler = (e) => {
    if (ScanService().isScanActive()) {
      return
    }

    dispatchShortcutKeydown(e)
  }

  window.addEventListener('keydown', shortcutsKeydownHandler)

  return unbindShortcutKeys
}

export {
  isAppShortcut,
  isGameShortcut,
  getShortcutKeys,
  bindShortcutKeys,
  handleShortcutKeydown,
  unbindShortcutKeys,
  registerShortcutAction,
}
